import React, { useCallback, useMemo, useState } from "react";
import PaymentDetailsModal from "./PaymentDetailsModal";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardBody, CardHeader, Col, Input, Label, Row } from "reactstrap";

import TableContainer from "Components/Common/TableContainer";
import { getPayments } from "common/services/payments.service";
import { getSortObj } from "common/lib/tableDataOptions";
import {
  buildListParams,
  parseFilter,
  parsePage,
  parsePageSize,
  parseSearch,
  parseSort,
} from "common/lib/listUrlParams";

import { isPaymentActive } from "common/lib/paymentOptions";
import Permission from "Components/Common/Permission";

import { normalize } from "common/lib/normalize";
import { toast } from "react-toastify";
import Filters from "./Filters";

const defaultFilterValue: FilterValue = {
  positionType: "",
  agency: "",
  dateRange: {
    fromDate: null,
    toDate: null,
  },
  region: "",
  mda: "",
  paymentCategory: "",
  thematicArea: "",
  sector: "",
  status: "",
  year: "",
};

const defaultSort = [{ id: "createdAt", desc: true }];

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  positionType: string;
  agency: string;
  dateRange: DateRange;
  region: string;
  mda: string;
  paymentCategory: string;
  thematicArea: string;
  sector: string;
  status?: string;
  year?: string;
}

// @ts-ignore
const TextWrapper: React.FC<{ value: string }> = ({ value }) => {
  if (!value) return null;

  return <div style={{ whiteSpace: "pre-wrap", width: "220px" }}>{value}</div>;
};

const MDAPayments = ({ mda }: { mda: any }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: parsePage(searchParams),
    pageSize: parsePageSize(searchParams),
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  // const setPage = (newPage: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageIndex: newPage - 1,
  //   }));
  // };

  const [sorted, setSorted] = useState(() => parseSort(searchParams.get("sort"), defaultSort));

  const { id: mdaId } = useParams();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  );

  const togglePaymentModal = () => {
    setIsPaymentModalOpen(!isPaymentModalOpen);
    if (isPaymentModalOpen) {
      setSelectedPaymentId(null);
    }
  };

  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = buildListParams(searchParams, {
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
        defaultFilterValue,
        defaultSort,
      });
      setSearchParams(params, {
        replace: true,
      });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.agency) {
        andConditions.push({
          agency: {
            id: {
              $eq: filterValue.agency.value,
            },
          },
        });
      }

      andConditions.push({
        mda: {
          id: {
            $eq: mdaId,
          },
        },
      });

      if (filterValue?.paymentCategory) {
        andConditions.push({
          paymentCategory: {
            id: {
              $eq: filterValue.paymentCategory.value,
            },
          },
        });
      }
      if (filterValue?.thematicArea) {
        andConditions.push({
          thematicArea: {
            id: {
              $eq: filterValue.thematicArea.value,
            },
          },
        });
      }
      if (filterValue?.sector) {
        andConditions.push({
          sector: {
            id: {
              $eq: filterValue.sector.value,
            },
          },
        });
      }
      if (filterValue?.positionType) {
        andConditions.push({
          positionType: {
            id: {
              $eq: filterValue.positionType.value,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        // Ensure we have Date objects before calling toLocaleDateString
        const fromDate =
          filterValue.dateRange.fromDate instanceof Date
            ? filterValue.dateRange.fromDate
            : new Date(filterValue.dateRange.fromDate);

        const toDate =
          filterValue.dateRange.toDate instanceof Date
            ? filterValue.dateRange.toDate
            : new Date(filterValue.dateRange.toDate);

        andConditions.push({
          $or: [
            {
              startDate: {
                $between: [
                  fromDate.toLocaleDateString("en-CA"),
                  toDate.toLocaleDateString("en-CA"),
                ],
              },
            },
            {
              endDate: {
                $between: [
                  fromDate.toLocaleDateString("en-CA"),
                  toDate.toLocaleDateString("en-CA"),
                ],
              },
            },
          ],
        });
      }

      if (filterValue?.status) {
        if (filterValue.status === "active")
          andConditions.push({
            startDate: {
              $lte: new Date().toISOString(),
            },
            endDate: {
              $gte: new Date().toISOString(),
            },
          });
        else if (filterValue.status === "inactive")
          andConditions.push({
            $or: [
              {
                startDate: {
                  $gt: new Date().toISOString(),
                },
              },
              {
                endDate: {
                  $lt: new Date().toISOString(),
                },
              },
            ],
          });
      }

      andConditions.push({
        $or: [
          {
            individual: {
              fullName: {
                $containsi: searchValue,
              },
            },
          },
          {
            positionTitle: {
              $containsi: searchValue,
            },
          },
          {
            mda: {
              name: {
                $containsi: searchValue,
              },
            },
          },
          {
            mda: {
              region: {
                name: {
                  $containsi: searchValue,
                },
              },
            },
          },
        ],
      });

      try {
        setLoading(true);

        const APIParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "agency",
            "mda",
            "mda.region",
            "individual",
            "positionType",
            "paymentCategory",
            "thematicArea",
            "sector",
          ],
          filters: {
            $and: andConditions,
          },
        };
        const response: any = await getPayments(APIParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [mdaId, pagination.pageSize, searchParams, setSearchParams]
  );

  const columns = useMemo(
    () => [
      {
        header: "Recipient Name",
        accessorKey: "individual.fullName",
        id: "individual.fullName",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  // whiteSpace: "pre-wrap",
                  width: "300px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title border bg-primary-subtle text-danger rounded-circle fs-24">
                      <i className="ri-user-line text-primary"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5 className="fs-14 mb-1" style={{ whiteSpace: "pre-wrap" }}>
                    <Permission
                      alternative={
                        <button
                          type="button"
                          className="text-body btn btn-link p-0 border-0"
                          onClick={() => {
                            setSelectedPaymentId(rowData.id);
                            setIsPaymentModalOpen(true);
                          }}
                        >
                          {cell.getValue()}
                        </button>
                      }
                      action="findOne"
                      resource="mda"
                    >
                      <button
                        type="button"
                        className="text-body btn btn-link p-0 border-0"
                        onClick={() => {
                          setSelectedPaymentId(rowData.id);
                          setIsPaymentModalOpen(true);
                        }}
                      >
                        {cell.getValue()}
                      </button>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    <span className="fw-medium">
                      {cell.row.original?.region?.name || ""}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },

      {
        header: "Position Title",
        accessorKey: "positionTitle",
        enableColumnFilter: false,
        id: "positionTitle",
        cell: (cell: any) => <TextWrapper value={cell.getValue() || ""} />,
      },

      {
        header: "Position Type",
        accessorKey: "positionType.name",
        enableColumnFilter: false,
        id: "positionType.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },
      {
        header: "Payment Category",
        accessorKey: "paymentCategory.name",
        enableColumnFilter: false,
        id: "paymentCategory.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },

      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const status = isPaymentActive(
            cell.row.original.startDate || "",
            cell.row.original.endDate || ""
          );
          return (
            <React.Fragment>
              {status === null ? (
                <span className="badge bg-warning-subtle text-warning fs-11">
                  Not Provided
                </span>
              ) : status ? (
                <span className="badge bg-success-subtle text-success fs-11">
                  Active
                </span>
              ) : status === false ? (
                <span className="badge bg-danger-subtle text-danger fs-11">
                  Not Active
                </span>
              ) : null}
            </React.Fragment>
          );
        },
      },
      {
        header: "Amount / Month",
        accessorKey: "amountPerMonth",
        enableColumnFilter: false,
        id: "amountPerMonth",
        cell: (cell: any) => {
          return (
            <>
              {cell.getValue()
                ? `$ ${cell.getValue().toLocaleString()}`
                : "Not Provided"}
            </>
          );
        },
      },
      {
        header: "Agency",
        accessorKey: "agency.name",
        enableColumnFilter: false,
        id: "agency.name",
        cell: (cell: any) => {
          return (
            <span className="text-primary">
              {cell.getValue() || "Not Provided"}
            </span>
          );
        },
      },
    ],
    []
  );

  const [searchValue, setSearchValue] = useState(parseSearch(searchParams));
  
  const [filterValue, setFilterValue] = useState(() => parseFilter(searchParams, defaultFilterValue));
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  return (
    <React.Fragment>
      <Card>
        <CardHeader>
          <Row className="align-items-center g-3">
            <Col md={8}>
              <h5 className="card-title mb-1">Payments</h5>
              <p className="mb-0 text-muted">
                A list of payments and contracts made to{" "}
                <span className="text-primary">
                  {mda?.name || "Not Provided"}
                </span>
              </p>
            </Col>
            <div className="col-md-auto ms-auto">
              <div className="d-flex gap-2">
                <div className="form-check form-switch form-switch-lg d-flex px-0 form-check-right align-items-center">
                  <Label
                    className="form-check-label me-3"
                    htmlFor="flexSwitchCheckCheckedDisabled2"
                    style={{ width: "120px", marginTop: "5px" }}
                  >
                    Active Payments
                  </Label>{" "}
                  <Input
                    name="isActive"
                    className="form-check-input me-2"
                    type="checkbox"
                    role="switch"
                    id="flexSwitchCheckCheckedDisabled2"
                    checked={filterValue.status === "active" ? true : false}
                    onChange={(e: any) => {
                      setFilterValue({
                        ...filterValue,
                        status: e.target.checked ? "active" : "",
                      });
                      setPage(1);
                    }}
                  />
                </div>
                {/* <Permission resource="payment" action="create"> */}
                {/* <Link
                      to="#"
                      className="btn btn-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        showAddModalForm();
                      }}
                    >
                      <i className="ri-add-line align-bottom me-1"></i>
                      Add Payment
                    </Link> */}
                {/* </Permission> */}

                {/* <Permissions
                      checks={[
                        {
                          resource: "payment",
                          action: "import",
                        },
                        {
                          resource: "payment",
                          action: "export",
                        },
                      ]}
                    > */}

                {/* </Permissions> */}
              </div>
            </div>
          </Row>
        </CardHeader>
        <CardBody>
          <Filters
            setPage={setPage}
            setSearchValue={setSearchValue}
            searchValue={searchValue}
            setFilterValue={setFilterValue}
            filterValue={filterValue}
            defaultFilterValue={defaultFilterValue}
            setSorted={setSorted}
          />
        </CardBody>
        <CardBody>
          <TableContainer
            loading={loading}
            columns={columns}
            data={data || []}
            pagination={pagination}
            setPagination={setPagination}
            sorted={sorted}
            setSorted={setSorted}
            onPageChange={onPageChange}
            FilterSection={() => {}}
            totalRows={meta?.pagination?.total || 0}
            totalPages={meta?.pagination?.pageCount || 0}
            searchValue={searchValue}
            filterValue={filterValue}
            divClass="table-responsive table-card mb-4"
            tableClass="align-middle table-nowrap mb-0 table-striped"
            theadClass="table-light table-nowrap"
          />
        </CardBody>
      </Card>

      {/* Payment Details Modal */}
      <PaymentDetailsModal
        isOpen={isPaymentModalOpen}
        toggle={togglePaymentModal}
        paymentId={selectedPaymentId}
      />
    </React.Fragment>
  );
};

export default MDAPayments;
